import './tenant-ratesheets.scss'

import { Button, Checkbox, Divider, notification, Skeleton, Typography } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { CheckboxValueType } from 'antd/lib/checkbox/Group'
import React, { useEffect, useState } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetchDetailByDomain } from '../../../api'
import { renderTenantPathLink } from '../../../components/tables/table-column-renderers'
import { httpClient } from '../../../util/http-client'
import { messages } from '../messages'

const CheckboxGroup = Checkbox.Group

interface Props {
  tenantDomain: string
  tenantName: string
  onSuccess: () => void
}

export const UpdateTenantRatesheet = ({ tenantDomain, tenantName, onSuccess }: Props) => {
  const { data: ratesheets, error } = useSWR(
    tenantDomain ? [`/ratesheets/me`, tenantDomain] : null,
    fetchDetailByDomain,
  )

  if (error) {
    return (
      <Column>
        <Typography.Title level={2}>{tenantName}</Typography.Title>
        <p>We found and error: {error.error_message}</p>
        <p>Please check the tenant setting</p>
      </Column>
    )
  }

  if (!ratesheets) {
    return <Skeleton />
  }

  const segmentCodes = Array.from(
    new Set(['CORP', 'FUND', 'BANK', 'LOCALGOV', ...(ratesheets?.segmentCodes || [])]),
  ).sort()

  return (
    <Column className="tenant-ratesheets">
      <Typography.Title level={2}>{tenantName}</Typography.Title>

      {!ratesheets?.list?.length && (
        <Column>
          <h3>No ratesheet instruments enabled for this tenant</h3>
          <p>
            In the future you will be able to to enable the ratesheets instruments as well as individual segments here
          </p>
          <h3>How to enable ratesheets segments here</h3>
          <ol>
            <li>Go to the {renderTenantPathLink(tenantDomain, '/ratesheets/update')} page</li>
            <li>Switch "Yes" to the "Available" segments that are required to update</li>
            <li>Click the "Save changed rates" button. Even if no rate were changed</li>
          </ol>
          <p>On save it will display the available segments here</p>
        </Column>
      )}

      {ratesheets?.list &&
        ratesheets?.list.map(({ instrumentCode, ratesheet }) => (
          <div key={instrumentCode}>
            <RatesheetToggle
              tenantDomain={tenantDomain}
              segmentCodes={segmentCodes}
              instrumentCode={instrumentCode}
              ratesheet={ratesheet}
              onSuccess={onSuccess}
            />
            <Divider />
          </div>
        ))}
    </Column>
  )
}

const RatesheetToggle = ({ tenantDomain, segmentCodes, instrumentCode, ratesheet, onSuccess }) => {
  const [selectedOnServer, setSelectedOnServer] = useState([])
  const [selectedOnForm, setSelectedOnForm] = useState([])

  const [hasPartialSelection, setHasPartialSelection] = useState(true)
  const [isAllSelected, setIsAllSelected] = useState(false)

  useEffect(() => {
    const checked = buildSegmentAvailableOptions({ ratesheet })

    setSelectedOnServer(checked)
    setSelectedOnForm(checked)
    setHasPartialSelection(!!checked.length && checked.length < segmentCodes.length)
    setIsAllSelected(checked.length === segmentCodes.length)
  }, [ratesheet, segmentCodes])

  const onCheckAllOption = (event: CheckboxChangeEvent) => {
    setSelectedOnForm(event.target.checked ? segmentCodes : [])
    setHasPartialSelection(false)
    setIsAllSelected(event.target.checked)
  }

  const onCheckOption = (checkedValues: CheckboxValueType[]) => {
    setSelectedOnForm(checkedValues)
    setHasPartialSelection(!!checkedValues.length && checkedValues.length < segmentCodes.length)
    setIsAllSelected(checkedValues.length === segmentCodes.length)
  }

  return (
    <Column className="ratesheet">
      <Row alignItemsCenter justifyContentSpaceBetween>
        <Typography.Title level={3}>{instrumentCode}</Typography.Title>
        <Checkbox indeterminate={hasPartialSelection} onChange={onCheckAllOption} checked={isAllSelected}>
          {`${isAllSelected ? 'Uncheck' : 'Check'} all ${instrumentCode}'s Segments`}
        </Checkbox>
        <Button
          type="primary"
          onClick={() =>
            onUpdateRatesheets({
              tenantDomain,
              instrumentCode,
              ratesheet,
              availableSegments: segmentCodes,
              current: selectedOnServer,
              changed: selectedOnForm,
              onSuccess,
            })
          }
        >
          Update {instrumentCode} Ratesheet
        </Button>
      </Row>

      <CheckboxGroup
        options={segmentCodes}
        value={selectedOnForm}
        onChange={onCheckOption}
        className="licenses-checkboxes"
      />
    </Column>
  )
}

const buildSegmentAvailableOptions = ({ ratesheet }) => {
  if (!ratesheet) return []

  return ratesheet?.filter(({ available }) => available).map(({ segmentCode }) => segmentCode)
}

const onUpdateRatesheets = async ({
  tenantDomain,
  instrumentCode,
  current,
  changed,
  ratesheet,
  availableSegments,
  onSuccess,
}) => {
  const add = changed.filter((segment: string) => !current.includes(segment))
  const remove = current.filter((segment: string) => !changed.includes(segment))

  const changes = [...add, ...remove].reduce((updated, segmentCode: string) => {
    updated[segmentCode] = changed.includes(segmentCode)

    return updated
  }, {})

  if (!Object.keys(changes).length) {
    notification.info({ message: messages.common.info, description: messages.ratesheet.update.unchanged })
    return false
  }

  const payload = buildUpdateRatesheetPayload({ instrumentCode, ratesheet, availableSegments, changes })

  try {
    await httpClient('/ratesheets', { method: 'PUT', domain: tenantDomain, data: payload })
    notification.success({ message: messages.common.success, description: messages.ratesheet.update.success })
    onSuccess()
  } catch (error) {
    notification.error({ message: messages.common.error, description: messages.ratesheet.update.error })
  }
}

const buildUpdateRatesheetPayload = ({ instrumentCode, ratesheet, availableSegments, changes }) => {
  const updatedRatesheet = ratesheet.map(({ segmentCode, rates, available }) => ({
    segmentCode,
    instrumentCode,
    rates,
    available: Object.keys(changes).includes(segmentCode) ? changes[segmentCode] : available,
  }))

  const existingRatesheets = ratesheet.map(({ segmentCode }) => segmentCode)
  const nonExistentSegments = availableSegments.filter((segmentCode) => !existingRatesheets.includes(segmentCode))
  const nonExistentRatesheets = buildNonExistentRatesheet({ nonExistentSegments, instrumentCode })

  return [...updatedRatesheet, ...nonExistentRatesheets]
}

const buildNonExistentRatesheet = ({ nonExistentSegments, instrumentCode }) => {
  const tdRates = { '2Y': 0, '3Y': 0, '4Y': 0, '5Y': 0 }

  const nonTdRates = {
    '1M': 0,
    '2M': 0,
    '3M': 0,
    '4M': 0,
    '5M': 0,
    '6M': 0,
    '7M': 0,
    '8M': 0,
    '9M': 0,
    '10M': 0,
    '11M': 0,
    '12M': 0,
  }

  const rates = instrumentCode === 'TD' ? { ...nonTdRates, ...tdRates } : nonTdRates

  return nonExistentSegments.map((segmentCode) => ({
    segmentCode,
    instrumentCode,
    available: false,
    rates,
  }))
}
